import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, ScanQrCode, Flashlight, FlashlightOff, SearchCode, TextSearch } from "lucide-react";
import { useTranslation } from "react-i18next";
import UIButton from "./utilities/UIButton";
import { SingleValue } from "react-select";
import { Option } from "../models/types";
import SearchableSelect from "./utilities/SearchableSelectBox";
import { ILocation } from '../models/ILocation';

interface QRScannerProps {
    onResult: (decodedText: string) => void;
    scanOnload?: boolean;
    id?: string;
    compact?: boolean;
    searchController?: string;
    iconShakeInterval?: number;
    extraFilter?: { type: string, value: ILocation };//| ICard; add other types later
}

const qrConfig = { fps: 10, qrbox: { width: 300, height: 300 } };
let html5QrCode: Html5Qrcode | null = null;

const QRScanner: React.FC<QRScannerProps> = ({ onResult, searchController = 'asset', compact = false, scanOnload = false, id = 'reader', iconShakeInterval = undefined, extraFilter = undefined }) => {
    const { i18n } = useTranslation();
    // @ts-ignore
    const t = (txt: string) => i18n.t(txt);
    const scannerRef = useRef<Html5Qrcode | null>(null);   // <-- per-instance
    const [torchSupported, setTorchSupported] = useState(false);

    const [isScanning, setIsScanning] = useState<boolean>(false);
    const [flashlightOn, setFlashlightOn] = useState(false);
    const [showAdvance, setShowAdvance] = useState(false);
    const [pValue, setPValue] = useState<Option>({ label: '', value: '' });

    const qrCodeSuccessCallback = (decodedText: string) => {
        onResult(decodedText);
        stopScanning();
    };

    const qrCodeErrorCallback = (err: any) => {
        // console.log(err);
    };

    // after successful start() – detect support once
    async function computeTorchSupported() {
        const s = scannerRef.current;

        // @ts-ignore
        if (!s) return false;

        try {
            // 1) Capabilities (best signal)
            const caps = (s as any).getRunningTrackCapabilities?.();
            if (caps && Object.prototype.hasOwnProperty.call(caps, "torch")) {
                return true;
            }

            // 2) Non-blinking probe: ask for torch:false (should be a no-op on supported tracks)
            await (s as any).applyVideoConstraints({ advanced: [{ torch: false }] });
            const settings = (s as any).getRunningTrackSettings?.();
            if (settings && Object.prototype.hasOwnProperty.call(settings, "torch")) {
                return true;
            }

            // 3) Last resort (may blink): enable then disable immediately
            await (s as any).applyVideoConstraints({ advanced: [{ torch: true }] });
            await (s as any).applyVideoConstraints({ advanced: [{ torch: false }] });
            return true;
        } catch (err) {
            console.log("Error detecting torch support: " + String(err));

            return false;
        }
    }

    // toggle only when running and supported
    const toggleFlashlight = async (enable: boolean) => {
        const s = scannerRef.current;
        // @ts-ignore
        if (!s || !s.isScanning || !torchSupported) return;
        try {
            await (s as any).applyVideoConstraints({
                torch: enable,
                advanced: [{ torch: enable }],
            });
            setFlashlightOn(enable);
        } catch (err) {
            console.warn("Torch toggle failed:", err);
        }
    };

    const handleFlashlightToggle = () => {
        const newState = !flashlightOn;
        setFlashlightOn(newState);
        toggleFlashlight(newState);
    };

    useEffect(() => {
        scannerRef.current = new Html5Qrcode(id);            // create for this div

        const startIfNeeded = async () => {
            if (scanOnload) {
                // Wait a frame so the container has layout (non-zero width)
                await new Promise(r => requestAnimationFrame(r));
                await startScanning();
            }
        };
        startIfNeeded();

        return () => {
            // cleanup on unmount to release camera
            stopScanning(true).catch(() => { });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, scanOnload]);

    const startScanning = async () => {
        const s = scannerRef.current;
        if (!s || isScanning) return;
        try {
            setIsScanning(true);                       // make overlay visible
            await new Promise(r => requestAnimationFrame(r));
            await s.start(
                { facingMode: "environment" },
                qrConfig,
                (txt) => { onResult(txt); stopScanning().catch(() => { }); },
                () => { }
            );
            const torchOk = await computeTorchSupported();
            setTorchSupported(torchOk);
        } catch (e) {
            setIsScanning(false);
            console.error("Unable to start scanning:", e);
        }
    };

    const stopScanning = async (fromUnmount = false) => {
        const s = scannerRef.current;
        if (!s) return;
        if (s.isScanning) await s.stop();                     // stop camera/track
        await s.clear();                                      // detach video/canvas
        if (!fromUnmount) setIsScanning(false);
    };

    //FIXME: never used?
    const startScanning_ = () => {
        if (html5QrCode && !isScanning) {
            html5QrCode.start(
                { facingMode: 'environment' },
                qrConfig,
                qrCodeSuccessCallback,
                qrCodeErrorCallback
            )
                .then(() => {
                    setIsScanning(true);
                })
                .catch((err: any) => {
                    console.error(`Unable to start scanning: ${err}`);
                });
        }
    };

    const handleSetValue = (value: SingleValue<Option>) => {
        if (value) {
            setPValue({ label: value.label, value: value.value });
            onResult(value.value as string);
            setShowAdvance(false)
            stopScanning();
        }
    }

    //FIXME: never used?
    const stopScanning_ = () => {
        if (html5QrCode && (html5QrCode.isScanning || isScanning)) {
            html5QrCode.stop()
                .then(() => {
                    html5QrCode?.clear();
                    setIsScanning(false);
                })
                .catch((err: any) => {
                    console.warn(`Cannot stop, scanner is not running: ${err}`);
                });
        }
    };

    return (
        <>
            <div
                className="bg-dark text-white"
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1,
                    display: isScanning ? "flex" : "none", // flex container
                    alignItems: "center",                   // vertical center
                    justifyContent: "center",               // horizontal center
                    height: "100dvh",                       // dynamic viewport height
                }}
            >
                <div
                    id={id}
                    style={{
                        width: "100%",
                        position: "relative",
                    }}
                />
            </div>

            {isScanning ? (
                <div style={{
                    position: "fixed",
                    display: "flex",
                    justifyContent: "space-between",
                    bottom: 0,
                    left: 0,
                    zIndex: 2,
                    padding: 10,
                    width: "100%",
                }}>
                    <button type="button" className={"btn btn-danger " + (compact ? '' : 'mt-3')} onClick={() => stopScanning()}>
                        <X />
                    </button>
                    <button
                        type="button"
                        className={"btn btn-primary " + (compact ? "" : "mt-3")}
                        disabled={!isScanning || !torchSupported}
                        onClick={() => toggleFlashlight(!flashlightOn)}
                    >
                        {flashlightOn ? <FlashlightOff /> : <Flashlight />}
                    </button>
                </div>

            ) : (
                    <div className={"d-inline-grid"}>
                    <div className="d-inline-flex" style={{ gap: '4%' }}>
                        <UIButton
                            className={"btn btn-primary text-white " + (compact ? '' : 'mt-3 d-inline')}
                            style={compact ? { marginLeft: '10px' } : { width: '48%' }}
                            clickFunction={startScanning}
                            id='take-picture'
                            Icon={ScanQrCode}
                            label={compact ? undefined : "qr_scanning"}
                            shakeInterval={iconShakeInterval}
                        />
                        {compact ? <></> : <UIButton
                            className={"btn btn-primary text-white " + (compact ? '' : 'mt-3 d-inline')}
                            style={compact ? { display: 'none!important' } : { width: '48%' }}
                            clickFunction={() => setShowAdvance(!showAdvance)}
                            id='search-by-id'
                            Icon={TextSearch}
                            label={compact ? undefined : 'manual_search'}
                            shakeInterval={iconShakeInterval}
                        />}
                    </div>

                    <div style={{ display: showAdvance ? 'block' : 'none' }} className="p-2 rounded c-border mt-3">
                        <div className="mt-1 mx-auto p-1 mb-1 bg-light">{t("search") + " " + t(searchController || "")}</div>
                        <SearchableSelect controller={searchController || ""} wrap={false} extraFilter={extraFilter}
                            value={pValue} onSelect={handleSetValue} label={"Search Asset"} name={"asset_identifier"} />
                    </div>
                </div>
            )}
        </>
    );
};

export default QRScanner;
